import { Check, Sparkles, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface Props {
  /** La valeur entendue dans l'appel, déjà mise en forme pour l'affichage. */
  texte: string
  /** La phrase entendue, montrée au survol. */
  citation: string
  darkMode: boolean
  enCours?: boolean
  onApprouver: () => void
  onRefuser: () => void
}

/**
 * Une valeur de fiche proposée par l'IA, affichée DANS le champ vide, à la
 * place du texte d'invite : en couleur, avec ✓ pour l'approuver et ✕ pour la
 * refuser. Rien n'est écrit sans ce clic.
 */
export function ValeurProposee({ texte, citation, darkMode, enCours, onApprouver, onRefuser }: Props) {
  const { t } = useTranslation()
  const bouton = cn(
    'h-5 w-5 rounded flex items-center justify-center transition-colors disabled:opacity-50',
    darkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-100',
  )
  return (
    <span
      className="flex items-center gap-1 min-w-0"
      title={t('contacts.suggestions.heard', 'Entendu dans l’appel : « {{quote}} »', { quote: citation })}
    >
      <Sparkles className={cn('w-3 h-3 flex-shrink-0', darkMode ? 'text-indigo-400' : 'text-indigo-500')} />
      <span
        className={cn(
          'text-[11px] font-medium truncate max-w-[120px]',
          darkMode ? 'text-indigo-300' : 'text-indigo-600',
        )}
      >
        {texte}
      </span>
      <button
        type="button"
        disabled={enCours}
        onClick={(e) => {
          e.stopPropagation()
          onApprouver()
        }}
        className={cn(bouton, darkMode ? 'text-emerald-400' : 'text-emerald-600')}
        aria-label={t('contacts.suggestions.accept', 'Approuver')}
        title={t('contacts.suggestions.accept', 'Approuver')}
      >
        <Check className="w-3 h-3" strokeWidth={3} />
      </button>
      <button
        type="button"
        disabled={enCours}
        onClick={(e) => {
          e.stopPropagation()
          onRefuser()
        }}
        className={cn(bouton, darkMode ? 'text-slate-500' : 'text-gray-400')}
        aria-label={t('contacts.suggestions.reject', 'Refuser')}
        title={t('contacts.suggestions.reject', 'Refuser')}
      >
        <X className="w-3 h-3" strokeWidth={3} />
      </button>
    </span>
  )
}
